import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL, LOGO_SRC } from "@/constants/site";
import { scrollToTarget } from "@/lib/scroll";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const goTo = (id) => {
        setOpen(false);
        scrollToTarget(`#${id}`);
    };

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                    scrolled
                        ? "bg-[#0A0E17]/80 backdrop-blur-xl border-b border-white/10"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between gap-6">
                    <button
                        data-testid="logo-home-button"
                        onClick={() => goTo("accueil")}
                        className="flex items-center gap-3 shrink-0 group"
                        aria-label="Pare Brise Paris — Accueil"
                    >
                        <img
                            src={LOGO_SRC}
                            alt="Pare Brise Paris"
                            className="w-12 h-12 rounded-full object-cover ring-2 ring-gold/60 transition-transform duration-500 group-hover:scale-105"
                        />
                        <span className="hidden sm:block leading-tight">
                            <span className="block font-display font-semibold text-lg tracking-wide text-white">
                                PARE BRISE
                            </span>
                            <span className="block font-display font-semibold text-sm tracking-[0.35em] text-gold-gradient gold-shimmer">
                                PARIS
                            </span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-9" aria-label="Navigation principale">
                        {NAV_LINKS.map((link) => (
                            <button
                                key={link.id}
                                data-testid={`nav-link-${link.id}`}
                                onClick={() => goTo(link.id)}
                                className="relative text-sm font-medium tracking-wide text-gray-300 transition-colors duration-300 hover:text-gold after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-gold after:transition-[width] after:duration-300 hover:after:w-full"
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <motion.a
                            data-testid="header-phone-cta"
                            href={PHONE_TEL}
                            whileHover={{ scale: 1.04 }}
                            whileTap={{ scale: 0.95 }}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light px-5 py-2.5 text-sm font-semibold text-midnight-900 shadow-[0_0_25px_rgba(212,175,55,0.35)]"
                        >
                            <Phone size={16} strokeWidth={2.5} />
                            <span>{PHONE_DISPLAY}</span>
                        </motion.a>
                        <button
                            data-testid="hamburger-btn"
                            onClick={() => setOpen(!open)}
                            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-white/15 text-white"
                            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
                            aria-expanded={open}
                        >
                            {open ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0, y: -16 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -16 }}
                        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                        className="fixed top-20 inset-x-4 z-40 lg:hidden rounded-2xl border border-white/10 bg-[#0A0E17]/95 backdrop-blur-xl p-6 shadow-2xl"
                    >
                        <nav className="flex flex-col gap-1" aria-label="Navigation mobile">
                            {NAV_LINKS.map((link, i) => (
                                <motion.button
                                    key={link.id}
                                    data-testid={`mobile-nav-link-${link.id}`}
                                    initial={{ opacity: 0, x: -12 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.06 * i }}
                                    onClick={() => goTo(link.id)}
                                    className="text-left font-display text-2xl py-3 text-gray-200 border-b border-white/5 transition-colors hover:text-gold"
                                >
                                    {link.label}
                                </motion.button>
                            ))}
                        </nav>
                        <a
                            data-testid="mobile-menu-phone-cta"
                            href={PHONE_TEL}
                            className="mt-5 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light px-5 py-3.5 font-semibold text-midnight-900"
                        >
                            <Phone size={18} strokeWidth={2.5} />
                            {PHONE_DISPLAY}
                        </a>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
